import { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Button, Heading, Text } from '@livechat/design-system-react-components';

import Amplitude from 'services/Amplitude';
import { ErrorResponse } from 'services/connectivity/types';
import warningIcon from '_assets/images/warning-inactive.svg';
import { ApiErrors, ApiSubErrors, StandardError } from 'types/error';
import { LocalStorageKey } from 'constants/local-storage';
import { Product } from 'types/product';
import { PRODUCTS } from 'constants/product';
import { Logo } from 'components/logo/Logo';
import { HelpCenterFooter } from 'components/help-center/Footer';
import { ErrorMessageForm } from 'components/forms/error-message-form/ErrorMessageForm';
import {
  defaultProductAndOrganization,
  detectProduct,
  getAppLinkByName,
  getProductByName,
  getProductDisplayName,
  getProductName,
} from 'helpers/product';
import { useSharedStore } from 'stores/shared-store';
import { getItem, removeItem } from 'helpers/storage/local-storage';
import Location from 'services/Location';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { getAccessToken } from 'helpers/account';
import { parseJwt } from 'helpers/token';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import LocalStorage from 'services/LocalStorage';
import { organizationsClient } from 'services/connectivity/organizations/client';
import { accountsClient } from 'services/connectivity/accounts/client';

import { parseTraceId } from 'helpers/headers';
import { OrganizationChooser } from '../chooser/OrganizationChooser';

import * as styles from './styles';

declare const window: any;

export const ActivateProduct: FC = () => {
  const [product, setProduct] = useState<string | null>(null);
  const [currentProductCookie, setCurrentProductCookie] = useState<Product | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [redirectParams, setRedirectParams] = useState<string | null>(null);
  const [isSigningUp, setIsSigningUp] = useState<boolean | null>(null);
  const [, setOrganizationWithJoinBlocked] = useState<string | null>(null);
  const [accountProducts, setAccountsProducts] = useState<string[] | null>(null);
  const [error, setError] = useState<StandardError | null>(null);

  const { forProduct } = useSharedStore();

  useEffect(() => {
    const pathProduct = PRODUCTS.includes(Location.getLastUrlParam())
      ? Location.getLastUrlParam()
      : null;
    const checkIsSigningUp =
      getItem('signup') === 1 ||
      Location.getUrlParam('signup') === '1' ||
      Location.getHashParam('signup') === '1';

    const state = Location.getHashParam('state') || Location.getUrlParam('state');
    const jwt = parseJwt(state);

    setProduct(pathProduct || Location.getUrlParam('product') || Location.getHashParam('product'));
    setCurrentProductCookie(detectProduct());
    setAccessToken(getAccessToken());
    setRedirectParams(jwt);
    setIsSigningUp(checkIsSigningUp || false);
  }, []);

  useEffect(() => {
    const activateRequestedProduct = async () => {
      if (!isSigningUp) {
        return;
      }

      if (!product) {
        setError({ error: ApiErrors.ResourceNotFound });
        return;
      }

      if (!accessToken) {
        setError({ error: ApiErrors.Unauthorized });
        return;
      }

      const {
        result: account,
        error: getMeError,
        response: getMeResponse,
      } = await accountsClient.getMe(accessToken);

      if (getMeError) {
        setError({
          error: getMeError.error,
          description: getMeError.sub_error,
          requestId: getMeError.request_id,
          traceId: parseTraceId(getMeResponse.headers),
        });
        return;
      }

      const roles = account.roles ?? [];
      const availableProducts = [];
      const { email } = account;

      // eslint-disable-next-line no-restricted-syntax
      for (const role of roles) {
        if (role.product !== 'Accounts') {
          availableProducts.push(role.product);
        }
      }
      setAccountsProducts(availableProducts);

      const utmParams: { [key: string]: any } = Location.getUtmHashParams();
      const couponCode = LocalStorage.get(LocalStorageKey.HdCouponCode);

      if (product === getProductName(Product.HelpDesk) && couponCode) {
        utmParams.coupon_code = couponCode;
        LocalStorage.remove(LocalStorageKey.HdCouponCode);
      }

      const { error: activateError, response } = await organizationsClient.activateProduct(
        {
          product,
          tracking: Object.keys(utmParams)?.length > 0 ? utmParams : forProduct(product, false),
        },
        accessToken
      );

      if (activateError) {
        setError({
          error: activateError.error,
          description: activateError.sub_error,
          requestId: activateError.request_id,
          traceId: parseTraceId(response?.headers),
        });
        setProduct((prevState) => activateError.resource_id || prevState);
        handleActivateError(account?.email, product, activateError);
        return;
      }

      handleSignupAmplitude(email, product);
      pushExternalRedirectToDataLayer(getAppLinkByName(product));

      const isActivatingKB = product === 'KnowledgeBase' && currentProductCookie === 3;

      if (!isActivatingKB) {
        removeItem('signup');
      }

      Cookies.remove('product');

      if (redirectParams) {
        const redirectURL = `${window.location.origin}?${buildOauthQuery(redirectParams)}`;

        window.location.replace(redirectURL);

        return;
      }

      window.location.replace(window.location.href);
    };

    activateRequestedProduct();
  }, [isSigningUp]);

  const handleSignupAmplitude = (email: string, productToActivate: string): void => {
    Amplitude.logEvent(
      email,
      'ACTIVATE_PRODUCT',
      {
        product: productToActivate,
      },
      true
    );
  };

  const handleActivateError = (
    email: string,
    productToActivate: string,
    activationError: ErrorResponse
  ): void => {
    Amplitude.logEvent(
      email,
      'Accounts: Activate product error',
      {
        error: activationError.error,
        subError: activationError.sub_error,
        product: productToActivate,
      },
      true
    );
  };

  const handleSetOrganizationWithJoinBlocked = (organizationId: string) => {
    setOrganizationWithJoinBlocked(organizationId);
  };

  const handleLoginRedirect = () => {
    defaultProductAndOrganization();
  };

  if (!isSigningUp) {
    return (
      <OrganizationChooser onJoinBlocked={handleSetOrganizationWithJoinBlocked} isAccountVerified />
    );
  }

  if (error) {
    const traceId = error.traceId ?? error.requestId;

    if (error.description === ApiSubErrors.InsufficientScope) {
      return (
        <div css={styles.missingAccessContainer}>
          <div css={styles.missingAccess}>
            <ErrorMessageForm
              titleCaption="Your organization doesn't use this product yet"
              titleContent="Ask your organization owner to get access."
              isMissingAccess
            >
              <div css={styles.orTextContainer}>
                <span>
                  <Text>or</Text>
                </span>
              </div>
              <Text css={styles.missingAccessLogin}>Log in to the products you already have:</Text>
            </ErrorMessageForm>
            <div css={styles.userProductsButtonsContainer}>
              {accountProducts?.map((accountProduct) => (
                <a
                  key={accountProduct}
                  href={getAppLinkByName(accountProduct)}
                  target="_self"
                  rel="noreferrer"
                >
                  <Button kind="secondary">
                    <div css={styles.stopScreenLogoContainer}>
                      <Logo product={getProductByName(accountProduct)} size="xlarge" />
                    </div>
                    Go to {getProductDisplayName(accountProduct)}
                  </Button>
                </a>
              ))}
            </div>
          </div>
          <HelpCenterFooter />
        </div>
      );
    }

    if (
      error.description === ApiSubErrors.LimitReached ||
      error.description === ApiSubErrors.ResourceLimitReached
    ) {
      return (
        <ErrorMessageForm
          titleCaption="You’ve reached your sign up limit"
          titleContent="Check back later!"
          imgSrc={warningIcon}
          contactSupportButton
          backToLogin
        />
      );
    }

    if (
      error.error === ApiErrors.AccessDenied ||
      error.description === ApiSubErrors.CannotInstallProduct
    ) {
      return window.location.replace(import.meta.env.VITE_APP_TA_APP_URL);
    }

    switch (error.error) {
      case ApiErrors.Conflict:
        return (
          <ErrorMessageForm
            titleCaption="Sorry, you can’t sign up for LiveChat"
            titleContent="Your email is already taken."
            traceId={traceId}
            link="/"
            buttonText="Log in"
            backToLogin
            onClick={handleLoginRedirect}
          />
        );

      case ApiErrors.Unauthorized:
      case ApiErrors.ResourceNotFound:
        return (
          <ErrorMessageForm
            titleCaption="This link seems broken"
            titleContent="Did you try to log in?"
            traceId={traceId}
            link="/"
            buttonText="Log in"
            backToLogin
            onClick={handleLoginRedirect}
          />
        );

      default:
        return (
          <ErrorMessageForm
            titleCaption="Product not activated"
            titleContent={
              <>
                You tried to activate&nbsp;<strong>{getProductDisplayName(product)}</strong>
                &nbsp;product.
                <br /> Please try again later.
              </>
            }
            traceId={traceId}
            backToLogin
          />
        );
    }
  }

  if (product === getProductName(Product.PartnerProgram)) {
    return (
      <div>
        <div css={styles.activatingContainer}>
          <Heading size="lg">Creating your Partner profile</Heading>
          <Text>In a moment, you will be taken to the Partner App.</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div css={styles.activatingContainer}>
        <Heading size="lg">Activating product...</Heading>
        <Text>
          We are about to activate <strong>{getProductDisplayName(product)}</strong> for your
          organization!
        </Text>
      </div>
    </div>
  );
};
